import axios, { AxiosResponse } from 'axios';
import {
  Mold,
  ProductPart,
  Task,
  SelectionConfig,
  MoldCreationData,
} from '../../types/mold';
import { MOLD_TYPES } from '../../constants/mold';
import { GATE_TYPES } from '../../constants/gateType';
import { noteMappingOptions } from '../../constants/moldOrder';
import { MoldStats } from '../../types/statistics';
import { ApiResponse } from '@/types/api';
import { buildErrorPayload } from '../../utils/error';
import api from './client';

export interface MoldImage {
  id: number;
  fileMetadataId: number;
  fileName: string;
  originalFileName?: string;
  position: number;
  isCover: boolean;
  category?: string | null;
  caption?: string | null;
}

export const moldImageService = {
  list: (moldId: string) => api.get<MoldImage[]>(`/molds/${moldId}/images`),
  sync: (moldId: string, fileNames: string[]) => api.post<MoldImage[]>(`/molds/${moldId}/images/sync`, { fileNames }),
  reorder: (moldId: string, imageIds: number[]) => api.put(`/molds/${moldId}/images/order`, { imageIds }),
  update: (moldId: string, imageId: number, data: Partial<Pick<MoldImage, 'caption' | 'category' | 'isCover'>>) =>
    api.patch(`/molds/${moldId}/images/${imageId}`, data),
};

export interface MoldAttachment {
  id: number;
  moldId: number;
  fileMetadataId: number;
  fileName: string;
  originalFileName?: string | null;
  title?: string | null;
  category?: string | null;
  position: number;
  fileType?: string | null;
  fileSize?: number | null;
  uploadedAt?: string | null;
  uploadedBy?: number | null;
  uploadedByName?: string | null;
}

export interface MoldAttachmentSyncItem {
  fileName: string;
  title?: string | null;
  category?: string | null;
}

export const moldAttachmentService = {
  list: (moldId: string) =>
    api.get<MoldAttachment[]>(`/molds/${moldId}/attachments`),
  sync: (moldId: string, files: MoldAttachmentSyncItem[]) =>
    api.post<MoldAttachment[]>(`/molds/${moldId}/attachments/sync`, {
      files,
    }),
  update: (
    moldId: string,
    attachmentId: number,
    data: Partial<Pick<MoldAttachment, 'title' | 'category'>>,
  ) => api.patch(`/molds/${moldId}/attachments/${attachmentId}`, data),
};

export interface MoldGuidePage {
  id: number;
  moldId: number;
  title: string;
  position: number;
  currentVersionNo: number;
  createdAt?: string | null;
  updatedAt?: string | null;
  updatedBy?: number | null;
  updatedByName?: string | null;
  deletedAt?: string | null;
  deletedByName?: string | null;
}

export interface MoldGuidePageDetail extends MoldGuidePage {
  content: unknown;
}

export interface MoldGuidePageVersion {
  id: number;
  pageId: number;
  versionNo: number;
  title: string;
  createdAt?: string | null;
  createdBy?: number | null;
  createdByName?: string | null;
}

export interface MoldGuidePageVersionDetail extends MoldGuidePageVersion {
  content: unknown;
}

export const moldGuideService = {
  list: (moldId: string) =>
    api.get<MoldGuidePage[]>(`/molds/${moldId}/guide-pages`),
  get: (moldId: string, pageId: number) =>
    api.get<MoldGuidePageDetail>(`/molds/${moldId}/guide-pages/${pageId}`),
  create: (moldId: string, data: { title: string; content: unknown }) =>
    api.post<MoldGuidePageDetail>(`/molds/${moldId}/guide-pages`, data),
  save: (
    moldId: string,
    pageId: number,
    data: { title: string; content: unknown; baseVersion: number },
  ) =>
    api.put<MoldGuidePageDetail>(
      `/molds/${moldId}/guide-pages/${pageId}`,
      data,
    ),
  // Soft delete: the page is hidden but recoverable via undelete.
  remove: (moldId: string, pageId: number) =>
    api.delete(`/molds/${moldId}/guide-pages/${pageId}`),
  listDeleted: (moldId: string) =>
    api.get<MoldGuidePage[]>(`/molds/${moldId}/guide-pages`, {
      params: { deleted: true },
    }),
  getDeleted: (moldId: string, pageId: number) =>
    api.get<MoldGuidePageDetail>(`/molds/${moldId}/guide-pages/${pageId}`, {
      params: { deleted: true },
    }),
  undelete: (moldId: string, pageId: number) =>
    api.post<MoldGuidePageDetail>(
      `/molds/${moldId}/guide-pages/${pageId}/undelete`,
    ),
  listVersions: (moldId: string, pageId: number) =>
    api.get<MoldGuidePageVersion[]>(
      `/molds/${moldId}/guide-pages/${pageId}/versions`,
    ),
  getVersion: (moldId: string, pageId: number, versionNo: number) =>
    api.get<MoldGuidePageVersionDetail>(
      `/molds/${moldId}/guide-pages/${pageId}/versions/${versionNo}`,
    ),
  restore: (moldId: string, pageId: number, versionNo: number) =>
    api.post<MoldGuidePageDetail>(
      `/molds/${moldId}/guide-pages/${pageId}/versions/${versionNo}/restore`,
    ),
};

export const moldService = {
  createMold: async (data: MoldCreationData): Promise<ApiResponse<Mold>> => {
    const { id, ...rest } = data;
    try {
      const result = await api.post('/molds', {
        ...rest,
        ...(rest?.customer?.id ? { customerId: rest?.customer?.id } : {}),
      });
      return { ...result, isSuccess: true } as ApiResponse<Mold>;
    } catch (error) {
      console.error('Error creating mold:', error);
      const body = axios.isAxiosError(error) ? error.response?.data : undefined;
      return buildErrorPayload(body) as ApiResponse<Mold>;
    }
  },
  getMolds: async (
    params: { withOpenOrder?: boolean } = { withOpenOrder: false },
  ): Promise<AxiosResponse<Mold[]>> => {
    return api.get('/molds', {
      params,
    });
  },

  getMoldById: async (
    id: string,
    params: { openMoldOrder?: boolean } = { openMoldOrder: false },
  ): Promise<AxiosResponse<Mold>> => {
    return api.get(`/molds/${id}`, {
      params,
    });
  },

  updateMold: async (
    id: string,
    data: Partial<Mold>,
  ): Promise<AxiosResponse<Mold>> => {
    const hasCustomerId = data?.customer?.id || data?.customerId;
    const customerId = data?.customerId || data?.customer?.id;
    const { customer, updatedAt, createdAt, ...rest } = data;
    return api.patch(`/molds/${id}`, {
      ...rest,
      ...(hasCustomerId && customerId
        ? { customerId: Number(customerId) }
        : {}),
    });
  },

  createTask: async (data: Omit<Task, 'id'>): Promise<AxiosResponse<Task>> => {
    return api.post('/tasks', data);
  },

  updateTaskStatus: async (
    id: string,
    status: Task['status'],
  ): Promise<AxiosResponse<Task>> => {
    return api.patch(`/tasks/${id}/status`, { status });
  },

  associatePart: async (
    moldId: string,
    partId: number,
  ): Promise<AxiosResponse<void>> => {
    return api.post(`/molds/${moldId}/product-parts/${partId}`);
  },

  deleteAssociatedPart: async (
    moldId: string,
    partId: number,
  ): Promise<AxiosResponse<void>> => {
    return api.delete(`/molds/${moldId}/product-parts/${partId}`);
  },

  associateInsert: async (
    moldId: string,
    insertId: number,
  ): Promise<AxiosResponse<void>> => {
    return api.post(`/molds/${moldId}/inserts/${insertId}`);
  },

  deleteAssociatedInsert: async (
    moldId: string,
    insertId: number,
  ): Promise<AxiosResponse<void>> => {
    return api.delete(`/molds/${moldId}/inserts/${insertId}`);
  },

  getMoldStats: async (): Promise<AxiosResponse<MoldStats>> => {
    return api.get('/molds/stats');
  },

  getMoldTypes: async (): Promise<{ value: string; label: string }[]> => {
    return MOLD_TYPES.map((moldType) => ({
      value: moldType,
      label: moldType,
    }));
  },

  getPlateTypes: async (): Promise<{ value: string; label: string }[]> => {
    return [
      { value: '2 plates', label: '2 plates' },
      { value: '3 plates', label: '3 plates' },
      { value: 'Stripper plates', label: 'Stripper plates' },
    ];
  },

  getGateTypes: async (): Promise<{ value: string; label: string }[]> => {
    return GATE_TYPES.map((gateType) => ({
      value: gateType,
      label: gateType,
    }));
  },

  getSurfaceFinishOptions: async (): Promise<
    { value: string; label: string }[]
  > => {
    return ['None', 'Mirror', 'Texture'].map((value) => ({
      value: value,
      label: value,
    }));
  },

  getInsertChangeTypeOptions: async (): Promise<
    { value: string; label: string }[]
  > => {
    return noteMappingOptions();
  },

  getSpringColors: async (): Promise<{ value: string; label: string }[]> => {
    return ['ขาว', 'เหลือง', 'น้ำตาล', 'ฟ้า', 'น้ำเงิน', 'เขียว'].map(
      (color) => ({
        value: color,
        label: color,
      }),
    );
  },

  getCountryList: async (): Promise<{ value: string; label: string }[]> => {
    return ['จีน', 'ไทย', 'ญี่ปุ่น', 'มาเลเซีย'].map((country) => ({
      value: country,
      label: country,
    }));
  },

  getMcSizeList: async (): Promise<{ value: number; label: string }[]> => {
    return [30, 50, 125, 130, 180, 220, 350].map((size) => ({
      value: size,
      label: String(size),
    }));
  },

  getSBIMoldSourceOptions: async (): Promise<
    { value: string; label: string }[]
  > => {
    return ['SBI Control', 'Customer supply'].map((source) => ({
      value: source,
      label: source,
    }));
  },

  getMoldSourceOptions: async (): Promise<
    { value: string; label: string }[]
  > => {
    return ['Internal Control', 'Customer supply'].map((source) => ({
      value: source,
      label: source,
    }));
  },

  getHotRunnerTypeOptions: async (): Promise<
    { value: string; label: string }[]
  > => {
    return ['Open', 'Valve'].map((type) => ({
      value: type,
      label: type,
    }));
  },

  getPartMatGradeOptions: async (): Promise<
    { value: string; label: string }[]
  > => {
    const response = await api.get('/selection-configs/product_part_materials');
    return response.data.values.map((item: SelectionConfig) => ({
      value: item.value,
      label: item.value,
    }));
  },
};

export const workflowService = {
  listWorkflows: async (): Promise<AxiosResponse<any>> => {
    return api.get('/workflows');
  },

  listWorkflowStates: async (): Promise<AxiosResponse<WorkflowStateCatalogResponse>> => {
    return api.get('/workflows/states');
  },

  getWorkflowById: async (id: number): Promise<AxiosResponse<any>> => {
    return api.get(`/workflows/${id}`);
  },

  getWorkflowByOrderType: async (
    orderType: string,
  ): Promise<AxiosResponse<any>> => {
    return api.get(`/workflows/by-order-type/${encodeURIComponent(orderType)}`);
  },
};

export interface WorkflowStateSource {
  id: number;
  name: string;
  version: number;
  is_active: boolean;
  is_latest_version: boolean;
}

export interface WorkflowStateCatalogItem {
  key: string;
  label: string;
  is_final: boolean;
  sources: WorkflowStateSource[];
}

export interface WorkflowStateCatalogResponse {
  states: WorkflowStateCatalogItem[];
}

export const productPartService = {
  getProductParts: async (): Promise<AxiosResponse<ProductPart[]>> => {
    return api.get('/product-parts');
  },

  getProductPartById: async (
    id: number,
  ): Promise<AxiosResponse<ProductPart>> => {
    return api.get(`/product-parts/${id}`);
  },

  createProductPart: async (data: {
    partNumber: string;
    partName: string;
    customerId: number;
    history?: string;
    model?: string;
    description?: string;
    status?: string;
    isActive?: boolean;
  }): Promise<AxiosResponse<ProductPart>> => {
    return api.post('/product-parts', data);
  },

  updateProductPart: async (
    id: number,
    data: {
      partNumber?: string;
      partName?: string;
      customerId?: number;
      history?: string;
      model?: string;
      description?: string;
      status?: string;
      isActive?: boolean;
    },
  ): Promise<AxiosResponse<ProductPart>> => {
    return api.put(`/product-parts/${id}`, data);
  },

  deleteProductPart: async (id: number): Promise<AxiosResponse<void>> => {
    return api.delete(`/product-parts/${id}`);
  },
};
